// resources/js/src/store/postStore.ts
import { create } from "zustand";
import { postApi } from "../services/post.api";
import { ApiError } from "../services/api";
import type {
  CommunityPost,
  CreatePostPayload,
  PostReaction,
  UpdatePostPayload,
} from "../services/post.api";
import type { ReactionType } from "../components/community/components/post/interfaces/types";

/*
|--------------------------------------------------------------------------
| Store Shape
|--------------------------------------------------------------------------
*/

type PostStoreState = {
  // Data
  posts: CommunityPost[];
  nextCursor: string | null;
  hasMore: boolean;

  // UI State
  isLoading: boolean;
  isLoadingMore: boolean;
  isSaving: boolean;
  error: string;

  // Actions
  loadFeed: (reset?: boolean) => Promise<void>;
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;

  createPost: (payload: CreatePostPayload) => Promise<CommunityPost | null>;
  updatePost: (
    id: number | string,
    payload: UpdatePostPayload
  ) => Promise<boolean>;
  deletePost: (id: number | string) => Promise<boolean>;

  // ✅ Reactions
  reactToPost: (
    postId: number | string,
    reaction: ReactionType
  ) => Promise<boolean>;

  // Local mutations
  prependPost: (post: CommunityPost) => void;
  replacePost: (post: CommunityPost) => void;
  removePost: (id: number) => void;

  clearError: () => void;
  reset: () => void;
};

/*
|--------------------------------------------------------------------------
| Helpers
|--------------------------------------------------------------------------
*/

function parseError(error: unknown): string {
  if (error instanceof ApiError) {
    return error.message;
  }
  return "Network error";
}

function parseMeta(raw: any): {
  next_cursor: string | null;
  prev_cursor: string | null;
  has_more: boolean;
  per_page: number;
} {
  const isObject = raw && typeof raw === "object" && !Array.isArray(raw);

  return {
    next_cursor: isObject ? raw.next_cursor ?? null : null,
    prev_cursor: isObject ? raw.prev_cursor ?? null : null,
    has_more: isObject ? raw.has_more ?? false : false,
    per_page: isObject ? raw.per_page ?? 15 : 15,
  };
}

function parseData(raw: any): CommunityPost[] {
  return Array.isArray(raw) ? raw : [];
}

/*
|--------------------------------------------------------------------------
| Store
|--------------------------------------------------------------------------
*/

export const usePostStore = create<PostStoreState>((set, get) => ({
  // ==================== Initial State ====================
  posts: [],
  nextCursor: null,
  hasMore: true,

  isLoading: false,
  isLoadingMore: false,
  isSaving: false,
  error: "",

  // ==================== Load Feed ====================
  loadFeed: async (reset = false) => {
    if (get().isLoading) return;

    set({
      isLoading: true,
      error: "",
      ...(reset ? { posts: [], nextCursor: null, hasMore: true } : {}),
    });

    try {
      const res = await postApi.loadFeed(null, 15);
      const data = parseData(res?.data);
      const meta = parseMeta(res?.meta);

      set({
        posts: data,
        nextCursor: meta.next_cursor,
        hasMore: meta.has_more,
        isLoading: false,
      });
    } catch (error) {
      set({
        error: parseError(error),
        isLoading: false,
      });
    }
  },

  // ==================== Load More ====================
  loadMore: async () => {
    const { nextCursor, hasMore, isLoadingMore, posts } = get();

    if (!hasMore || isLoadingMore || !nextCursor) return;

    set({ isLoadingMore: true });

    try {
      const res = await postApi.loadFeed(nextCursor, 15);

      const data = parseData(res?.data);
      const meta = parseMeta(res?.meta);

      set({
        posts: [...posts, ...data],
        nextCursor: meta.next_cursor,
        hasMore: meta.has_more,
        isLoadingMore: false,
      });
    } catch (error) {
      set({
        error: parseError(error),
        isLoadingMore: false,
      });
    }
  },

  // ==================== Refresh ====================
  refresh: async () => {
    set({ posts: [], nextCursor: null, hasMore: true });
    await get().loadFeed();
  },

  // ==================== Create ====================
  createPost: async (payload) => {
    set({ isSaving: true, error: "" });

    try {
      const post = await postApi.createPost(payload);

      set((state) => ({
        posts: [post, ...state.posts],
        isSaving: false,
      }));

      return post;
    } catch (error) {
      set({
        error: parseError(error),
        isSaving: false,
      });
      return null;
    }
  },

  // ==================== Update ====================
  updatePost: async (id, payload) => {
    set({ isSaving: true, error: "" });

    try {
      const post = await postApi.updatePost(id, payload);

      set((state) => ({
        posts: state.posts.map((p) => (p.id === post.id ? post : p)),
        isSaving: false,
      }));

      return true;
    } catch (error) {
      set({
        error: parseError(error),
        isSaving: false,
      });
      return false;
    }
  },

  // ==================== Delete ====================
  deletePost: async (id) => {
    set({ isSaving: true, error: "" });

    const numericId =
      typeof id === "string" ? parseInt(id, 10) : id;

    try {
      await postApi.deletePost(id);

      set((state) => ({
        posts: state.posts.filter((p) => p.id !== numericId),
        isSaving: false,
      }));

      return true;
    } catch (error) {
      set({
        error: parseError(error),
        isSaving: false,
      });
      return false;
    }
  },

  // ==================== React to Post ====================
  reactToPost: async (postId, reaction) => {
    // optimistic
    const previous = get().posts;

    set((state) => ({
      posts: state.posts.map((p) => {
        if (p.id.toString() !== postId.toString()) return p;

        const prevReaction = p.myReaction;
        let newReactions = p.reactions ?? [];

        // إزالة الـ reaction القديم
        if (prevReaction) {
          newReactions = newReactions.filter(
            (r) => r.user.id !== getCurrentUserId(previous, postId)
          );
        }

        // إضافة/تحديث
        if (reaction) {
          newReactions = [
            ...newReactions.filter(
              (r) => r.user.id !== getCurrentUserId(previous, postId)
            ),
            {
              id: Date.now(),
              type: reaction,
              user: {
                id: getCurrentUserId(previous, postId) ?? 0,
                name: "",
                avatarUrl: null,
              },
            },
          ];
        }

        return {
          ...p,
          myReaction: reaction,
          reactionsCount: Math.max(0, p.reactionsCount + (reaction ? (prevReaction ? 0 : 1) : -1)),
          reactions: newReactions,
        };
      }),
    }));

    try {
      const res = await postApi.reactToPost(postId, reaction);

      set((state) => ({
        posts: state.posts.map((p) =>
          p.id.toString() === postId.toString()
            ? {
                ...p,
                myReaction: res.myReaction ?? reaction,
                reactionsCount: res.reactionsCount,
                reactions: (res.reactions ?? p.reactions) as PostReaction[],
              }
            : p
        ),
      }));

      return true;
    } catch (error) {
      // rollback
      set({
        posts: previous,
        error: parseError(error),
      });
      return false;
    }
  },

  // ==================== Local Mutations ====================
  prependPost: (post) => {
    set((state) => ({ posts: [post, ...state.posts] }));
  },

  replacePost: (post) => {
    set((state) => ({
      posts: state.posts.map((p) => (p.id === post.id ? post : p)),
    }));
  },

  removePost: (id) => {
    set((state) => ({
      posts: state.posts.filter((p) => p.id !== id),
    }));
  },

  // ==================== Utilities ====================
  clearError: () => set({ error: "" }),

  reset: () =>
    set({
      posts: [],
      nextCursor: null,
      hasMore: true,
      isLoading: false,
      isLoadingMore: false,
      isSaving: false,
      error: "",
    }),
}));

/**
 * مساعد: الحصول على user_id من الـ auth store
 */
function getCurrentUserId(
  posts: CommunityPost[],
  postId: number | string
): number | null {
  // نستخدم reactions list للـ optimistic update
  // (مش دقيق 100%، بس الـ backend هيصحح)
  return null;
}